<script setup lang="ts">
import { Siren } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref, watch } from "vue";
import { toast } from "vue-sonner";
import { breakGlass } from "@/api/me";
import FormError from "@/components/common/FormError.vue";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { t } from "@/copy/strings";

const open = defineModel<boolean>("open", { default: false });
const queryClient = useQueryClient();

const minutes = ref("30");
const reason = ref("");
const busy = ref(false);
const error = ref<unknown>(null);
const valid = computed(() => reason.value.trim().length >= 10);

watch(open, (v) => {
  if (v) {
    reason.value = "";
    minutes.value = "30";
    error.value = null;
  }
});

async function submit() {
  if (!valid.value) return;
  busy.value = true;
  error.value = null;
  try {
    await breakGlass({ minutes: Number(minutes.value), reason: reason.value.trim() });
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    toast.success(`Elevated for ${minutes.value} minutes. This is recorded in the audit log.`);
    open.value = false;
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent>
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2"><Siren class="size-5 text-destructive" /> {{ t.admin.breakGlass }}</DialogTitle>
        <DialogDescription>{{ t.admin.breakGlassDescription }}</DialogDescription>
      </DialogHeader>
      <form class="grid gap-4" @submit.prevent="submit">
        <div class="grid gap-2">
          <Label>Duration</Label>
          <ToggleGroup v-model="minutes" type="single" variant="outline" class="justify-start">
            <ToggleGroupItem value="15">15 min</ToggleGroupItem>
            <ToggleGroupItem value="30">30 min</ToggleGroupItem>
            <ToggleGroupItem value="60">60 min</ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div class="grid gap-2">
          <Label for="bg-reason">{{ t.common.reason }}</Label>
          <Textarea id="bg-reason" v-model="reason" rows="3" placeholder="What's the emergency? (at least 10 characters)" />
        </div>
        <FormError :error="error" />
        <DialogFooter>
          <Button type="button" variant="ghost" @click="open = false">{{ t.common.cancel }}</Button>
          <Button type="submit" variant="destructive" :disabled="!valid || busy">Elevate me</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
